import { useLocation } from 'wouter';
import type { Card, EditGroup } from '../lib/api';
import { handOff, titleOf } from '../lib/handoff';
import { INSTRUCTIONS_SLUG } from '../lib/knowledge';

/**
 * The Monday card of your edits to drafts, by kind (fix-73). Grouped by the desk with no model; each kind of change
 * has "Make this a rule", which opens the Drafter instructions with a line drafted at the end, and each fact you added
 * has "Make this knowledge", which opens a new article with it. Nothing is saved until you press Save there, so the
 * guard runs as on any save.
 */
export function EditsPanel({ card }: { card: Card }) {
  const [, navigate] = useLocation();
  const groups = (card.payload.groups ?? []) as EditGroup[];
  const why = `from your edits, card #${card.id}`;

  const rule = (g: EditGroup) => {
    handOff({ kind: 'rule', slug: INSTRUCTIONS_SLUG, line: g.rule_line, reason: `${g.label.toLowerCase()} — ${why}` });
    navigate('/knowledge');
  };
  const knowledge = (fact: string) => {
    handOff({ kind: 'knowledge', title: titleOf(fact), body: fact, reason: `a fact added to a draft — ${why}` });
    navigate('/knowledge/new');
  };

  return (
    <section className="panel panel-grow edits">
      <div className="panel-head">
        <h3>Your edits, by kind</h3>
        <span className="mono panel-note">{String(card.payload.edits ?? 0)} saved edits · no model</span>
      </div>
      {groups.map((g) => (
        <div key={g.kind} className="edits-group">
          <div className="edits-group-head">
            <strong>{g.label}</strong> <span className="mono">×{g.count}</span>
            <button type="button" className="btn-secondary btn-small" onClick={() => rule(g)}>Make this a rule</button>
          </div>
          <p className="mono edits-line">{g.rule_line}</p>
          <ul className="edits-pairs">
            {g.pairs.map((p) => (
              <li key={`${p.feedback_id}-${p.after}`}>
                <span className="mono edits-card">#{p.card_id}</span>
                {p.before && <del>{p.before}</del>}
                {p.before && ' → '}
                <ins>{p.after}</ins>
                {g.kind === 'fact' && (
                  <button type="button" className="btn-ghost btn-small" onClick={() => knowledge(p.after)}>Make this knowledge</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
